import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import TestimonialsSection from "@/components/TestimonialsSection";

const MATERIAUX = [
    "Marbre de Carrare", "Chêne massif", "Laiton brossé", "Terrazzo vénitien",
    "Plâtre teinté", "Travertin", "Bronze patiné", "Pierre de Bourgogne",
    "Velours mohair", "Cuir tanné végétal", "Tadelakt", "Verre soufflé",
];

export default function Home() {
    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section className="relative min-h-[88vh] flex items-center px-6 md:px-12 max-w-7xl mx-auto pt-12">
                <div className="grid md:grid-cols-12 gap-10 items-center w-full">
                    <div className="md:col-span-6 lg:col-span-5">
                        <span className="overline">Rénovation d'intérieurs haut de gamme</span>
                        <h1 className="mt-8 font-serif text-6xl md:text-7xl lg:text-8xl font-light tracking-tight leading-[1.02] text-brand-ink" data-testid="hero-title">
                            L'art de
                            <span className="italic font-light text-brand-sage"> rénover </span>
                            vos intérieurs.
                        </h1>
                        <p className="mt-8 text-xl text-brand-sageDark font-light leading-relaxed max-w-md">
                            Japet Agencement conçoit et orchestre la rénovation d'appartements et de maisons de prestige
                            à Paris et en Île-de-France.
                        </p>
                        <div className="mt-12 flex flex-wrap items-center gap-4">
                            <Link
                                to="/devis"
                                data-testid="hero-cta-devis"
                                className="group inline-flex items-center gap-3 px-9 py-5 bg-brand-ink text-brand-bg text-sm uppercase tracking-[0.22em] hover:bg-brand-sage transition-colors"
                            >
                                Demander un devis
                                <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                            </Link>
                            <Link
                                to="/approche"
                                data-testid="hero-cta-approche"
                                className="text-sm uppercase tracking-[0.22em] text-brand-ink hover:text-brand-sage transition-colors border-b border-brand-ink/30 pb-1"
                            >
                                Découvrir notre approche
                            </Link>
                        </div>
                    </div>
                    <div className="md:col-span-6 lg:col-span-7 relative">
                        <div className="grid grid-cols-5 gap-4 md:gap-6">
                            <div className="col-span-3 hover-zoom relative">
                                <img
                                    src="https://images.unsplash.com/photo-1663811395977-d5a1502eed34?auto=format&fit=crop&w=900&q=80"
                                    alt="Intérieur haut de gamme"
                                    className="w-full h-[480px] md:h-[560px] object-cover"
                                />
                            </div>
                            <div className="col-span-2 flex flex-col gap-4 md:gap-6">
                                <div className="hover-zoom flex-1">
                                    <img
                                        src="https://images.unsplash.com/photo-1700157381190-e9fc5ddab91e?auto=format&fit=crop&w=800&q=80"
                                        alt="Détail intérieur"
                                        className="w-full h-full min-h-[230px] object-cover"
                                    />
                                </div>
                                <div className="hover-zoom flex-1">
                                    <img
                                        src="https://images.unsplash.com/photo-1663811396733-4dc430f0115d?auto=format&fit=crop&w=800&q=80"
                                        alt="Cuisine sur mesure"
                                        className="w-full h-full min-h-[230px] object-cover"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* MANIFESTE — éditorial long-form */}
            <section className="py-28 md:py-36 max-w-7xl mx-auto px-6 md:px-12 border-t border-brand-border" data-testid="manifeste-section">
                <div className="grid md:grid-cols-12 gap-12 md:gap-20">
                    <div className="md:col-span-5">
                        <span className="overline">Notre signature</span>
                        <Reveal>
                            <p className="mt-10 font-serif text-3xl md:text-4xl italic font-light text-brand-ink leading-[1.25]">
                                « Faire d'un lieu un endroit que l'on a hâte de retrouver. »
                            </p>
                            <p className="mt-8 text-base text-brand-sageDark tracking-[0.18em] uppercase">— Japet Agencement</p>
                        </Reveal>
                    </div>
                    <div className="md:col-span-6 md:col-start-7 space-y-7 text-lg leading-relaxed text-brand-sageDark font-light">
                        <Reveal delay={80}>
                            <p>
                                Japet Agencement est une société française spécialisée dans la rénovation
                                d'appartements et de maisons haut de gamme à Paris et en Île-de-France.
                            </p>
                        </Reveal>
                        <Reveal delay={160}>
                            <p>
                                Nous prenons en charge la rénovation complète de votre bien — de la dépose à la
                                remise des clés — en sélectionnant nous-mêmes les artisans, en pilotant les délais
                                et en garantissant la qualité d'exécution à chaque étape. Toutes nos interventions
                                sont assurées en responsabilité civile professionnelle et bénéficient de la garantie
                                décennale.
                            </p>
                        </Reveal>
                        <Reveal delay={240}>
                            <p>
                                Vous avez un seul interlocuteur, un seul contrat, un seul budget tenu. Et un résultat
                                à la hauteur de l'exigence avec laquelle vous avez choisi votre lieu de vie.
                            </p>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* MATÉRIAUX MARQUEE — signature visuelle */}
            <section className="py-20 md:py-24 bg-brand-ink text-brand-bg overflow-hidden" data-testid="materiaux-section">
                <div className="max-w-7xl mx-auto px-6 md:px-12 mb-10">
                    <span className="overline" style={{ color: "#A8C0B0" }}>Matières & finitions</span>
                    <h2 className="mt-5 font-serif text-4xl md:text-5xl font-light max-w-3xl leading-tight">
                        Le travail
                        <span className="italic text-brand-sage"> de la matière </span>
                        nous obsède.
                    </h2>
                </div>
                <div className="relative">
                    <div className="flex marquee-track gap-16 whitespace-nowrap">
                        {[...MATERIAUX, ...MATERIAUX].map((m, i) => (
                            <span key={i} className="font-serif text-4xl md:text-5xl italic font-light flex items-center gap-16">
                                {m}
                                <span className="text-brand-sage text-2xl not-italic">·</span>
                            </span>
                        ))}
                    </div>
                </div>
            </section>

            {/* SPÉCIALITÉS — split éditorial */}
            <section className="py-28 md:py-36 max-w-7xl mx-auto px-6 md:px-12" data-testid="specialites-section">
                <div className="grid md:grid-cols-12 gap-12 md:gap-16 items-center">
                    <div className="md:col-span-6 hover-zoom">
                        <img
                            src="https://images.unsplash.com/photo-1663811396760-b6c84fa45ee9?auto=format&fit=crop&w=1200&q=80"
                            alt="Pièce d'exception rénovée"
                            className="w-full h-[600px] md:h-[720px] object-cover"
                        />
                    </div>
                    <div className="md:col-span-5 md:col-start-8">
                        <span className="overline">Nos terrains de jeu</span>
                        <h2 className="mt-5 font-serif text-5xl md:text-6xl font-light leading-tight text-brand-ink">
                            Là où le détail
                            <span className="italic block">fait la différence.</span>
                        </h2>
                        <ul className="mt-10 divide-y divide-brand-border border-y border-brand-border">
                            {[
                                { title: "Appartements haussmanniens", text: "Restitution de moulures, parquets point de Hongrie, refonte distributive." },
                                { title: "Maisons d'architecte", text: "Coordination tous corps d'état, plâtreries fines, menuiseries dessinées." },
                                { title: "Cuisines & salles d'eau", text: "Pièces signature, plans sur-mesure, pierre naturelle, robinetterie d'exception." },
                                { title: "Aménagement sur-mesure", text: "Ébénisterie, dressings, bibliothèques — réalisés par nos artisans partenaires." },
                            ].map((item) => (
                                <li key={item.title} className="py-6">
                                    <h3 className="font-serif text-2xl text-brand-ink">{item.title}</h3>
                                    <p className="mt-2 text-brand-sageDark font-light leading-relaxed text-base">{item.text}</p>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* CTA FINAL */}
            <TestimonialsSection />

            <section className="bg-brand-sage text-brand-bg py-24" data-testid="final-cta-section">
                <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-10 items-center">
                    <div className="md:col-span-7">
                        <h2 className="font-serif text-5xl md:text-6xl font-light leading-tight">
                            Vous avez un projet
                            <span className="italic"> en tête </span>?
                        </h2>
                        <p className="mt-5 text-lg text-brand-bg/80 max-w-xl font-light">
                            Confiez-le à une équipe qui en prend soin comme s'il s'agissait du sien.
                        </p>
                    </div>
                    <div className="md:col-span-5 md:text-right">
                        <Link
                            to="/devis"
                            data-testid="final-cta-button"
                            className="inline-flex items-center gap-3 px-9 py-5 bg-brand-ink text-brand-bg text-sm uppercase tracking-[0.22em] hover:bg-brand-bg hover:text-brand-ink transition-colors"
                        >
                            Démarrer mon projet
                            <ArrowUpRight size={16} />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
